/**
 * `useApiQuery`, but it comes back on its own — and it never blanks a number it
 * already has.
 *
 * WP-K2 and WP-K3 read once and re-read when the person does something. A dashboard
 * is the first screen here that re-reads because time passed, and three of that
 * hook's behaviours are wrong for this one:
 *
 *  1. **`status` goes back to `loading` on every re-run**, which would replace four
 *     tiles with skeletons every poll. `DESIGN.md` §9: a live dashboard "refreshes
 *     its numbers and updates its 'last updated' line". Blanking a dashboard on a
 *     timer teaches people to stop looking at it.
 *  2. **A failure clears `data`.** On a poll that means one dropped request erases
 *     numbers that were correct thirty seconds ago. Here the last good answer stays
 *     on screen with `staleAfterError` set, so the screen can say *"these are the
 *     figures from 09:42; the refresh at 09:47 failed"* — which is a true sentence,
 *     where an empty tile is a false one.
 *  3. **Nothing records when the answer arrived.** `docs/UI_PROCESS.md` §4 and
 *     `DESIGN.md` §8 both make a "last updated" line an acceptance criterion, and it
 *     has to be the time of the last *successful* read, not of the last attempt.
 *
 * ── The interval is not a decoration ────────────────────────────────────────
 *
 * `web/shapes/overview.md` §12 sets two of them and says why. The short version:
 * Task 36 writes an hour's aggregate row once an hour, so a 30-second poll re-reads
 * an unchanged row 119 times out of 120; running timers change on a keypress. One
 * interval would be wrong for one of them.
 *
 * ── A hidden tab makes no requests ──────────────────────────────────────────
 *
 * Spec 01 §12's acceptance criterion is "refresh không tạo tải quá mức", and the
 * cheapest request is the one a tab nobody is looking at does not make. The timer is
 * torn down on `visibilitychange` to hidden and one read fires immediately on the
 * way back, so returning to the tab shows fresh numbers rather than an interval's
 * worth of stale ones. `document.visibilityState` is read at subscribe time as well,
 * so a tab that was already hidden when the screen mounted does not start a timer it
 * would only have to cancel.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { WorkforceApiError } from './errors';
import { useSession } from '../auth/SessionProvider';

export interface PolledResult<T> {
  /**
   * `loading` only until the **first** answer. After that it is `ok` or `error` and
   * a refresh in flight is `refreshing`, not `loading` — the distinction is the
   * whole reason this hook exists.
   */
  readonly status: 'loading' | 'ok' | 'error';
  /**
   * The last successful body. It survives a failed refresh on purpose; read
   * `staleAfterError` before presenting it as current.
   */
  readonly data: T | null;
  readonly error: WorkforceApiError | null;
  /** A request is in flight and there is already something on screen. */
  readonly refreshing: boolean;
  /**
   * `data` is a previous success and the newest attempt failed. Exactly the
   * condition under which a number must be labelled rather than hidden.
   */
  readonly staleAfterError: boolean;
  /** When `data` arrived. `null` before the first success. The "last updated" line. */
  readonly fetchedAt: number | null;
  readonly reload: () => void;
}

const IDLE: PolledResult<never> = {
  status: 'loading',
  data: null,
  error: null,
  refreshing: false,
  staleAfterError: false,
  fetchedAt: null,
  reload: (): void => undefined,
};

/**
 * `path === null` means "there is nothing to ask yet" — the same convention
 * `useApiQuery` uses, and for the same reason: it is not an error and not an empty
 * result, so the hook parks with no request in flight and no timer running.
 *
 * `intervalMs <= 0` polls never, which is how a caller turns one region into a
 * read-once region without a second hook.
 */
export function usePolledQuery<T>(
  path: string | null,
  intervalMs: number,
  reloadToken = 0,
): PolledResult<T> {
  const { client } = useSession();
  const [state, setState] = useState<{
    readonly status: 'loading' | 'ok' | 'error';
    readonly data: T | null;
    readonly error: WorkforceApiError | null;
    readonly fetchedAt: number | null;
  }>({ status: 'loading', data: null, error: null, fetchedAt: null });
  const [refreshing, setRefreshing] = useState(false);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    if (path === null) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const controllers = new Set<AbortController>();

    const run = async (): Promise<void> => {
      const controller = new AbortController();
      controllers.add(controller);
      generation.current += 1;
      const mine = generation.current;
      setRefreshing(true);
      try {
        const body = await client.request<T>(path, { signal: controller.signal });
        if (cancelled || generation.current !== mine) return;
        setState({ status: 'ok', data: body, error: null, fetchedAt: Date.now() });
      } catch (cause) {
        if (cancelled || generation.current !== mine) return;
        // `client.request` re-throws an abort untouched so it can be told apart from
        // a network failure here. An abort is this hook's own doing and must never
        // become an outage on screen.
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        const error =
          cause instanceof WorkforceApiError
            ? cause
            : new WorkforceApiError({
                code: 'CLIENT_TRANSPORT_FAILED',
                message: cause instanceof Error ? cause.message : 'request failed',
                status: 0,
              });
        // `data` is deliberately carried forward. See the header, point 2.
        setState((previous) => ({ ...previous, status: 'error', error }));
      } finally {
        controllers.delete(controller);
        if (!cancelled && generation.current === mine) setRefreshing(false);
      }
    };

    const start = (): void => {
      if (timer !== null || intervalMs <= 0) return;
      timer = setInterval(() => void run(), intervalMs);
    };
    const stop = (): void => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };
    const hidden = (): boolean =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden';

    const onVisibility = (): void => {
      if (hidden()) {
        stop();
        return;
      }
      // One read on the way back, then the interval. Without the immediate read a
      // person returning to the tab reads numbers up to a whole interval old with a
      // "last updated" line that says so — true, but avoidable.
      void run();
      start();
    };

    void run();
    if (!hidden()) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      for (const controller of controllers) controller.abort();
    };
  }, [client, path, intervalMs, nonce, reloadToken]);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  if (path === null) return { ...(IDLE as PolledResult<T>), reload };

  return {
    status: state.status,
    data: state.data,
    error: state.error,
    refreshing: refreshing && state.fetchedAt !== null,
    staleAfterError: state.status === 'error' && state.data !== null,
    fetchedAt: state.fetchedAt,
    reload,
  };
}
